import React, { useState, useEffect, useRef } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { userService } from "../../../_services";
import AssignGrandParentRow from "./AssignGrandparentRow";
import { Button } from "@/components/ui/button";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";

function AssignAsGrandParent({ grandparentsList, secondLevel, repUserName, repId, toggle_gp_popup }) {
    const [childListData, setChildListData] = useState([]);
    const [saveLoader, setSaveLoader] = useState(false);
    const [errors, setErrors] = useState([]);
    const tbodyRef = useRef(null);

    useEffect(() => {
        if (grandparentsList?.length) {
            const transformedData = grandparentsList.map(item => ({
                UserName: item.userName,
                UserId: String(item.userId),
                Email: item.email,
                Comm: item.gcComm,
                Enable: item.gpAdded === "Yes"
            }));
            setChildListData(transformedData);
        }
    }, [grandparentsList]);

    const AssignGPUsers = () => {
        setSaveLoader(true);
        try {
            const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
            userService.UpdateGrandParentAdmin(
                Usermeta_Data.split(':')[0],
                Usermeta_Data.split(':')[1],
                repId,
                repUserName,
                secondLevel,
                childListData
            ).then(apidata => {
                setSaveLoader(false);
                if (apidata.length > 0) {
                    setErrors(apidata);
                    const allSuccess = apidata.every(item => item.code === "1");
                    if (allSuccess) {
                        setTimeout(() => { toggle_gp_popup(); }, 3000);
                    }
                } else {
                    setErrors([{ code: '-1', message: 'Some error occurred in submitting' }]);
                }
            });
        } catch (error) {
            setSaveLoader(false);
            setErrors([{ code: '-1', message: 'Some error occurred in submitting' }]);
        }
    };

    const handleToggleGP = (userName) => {
        try {
            setChildListData(prev =>
                prev.map(item =>
                    item.UserName === userName ? { ...item, Enable: !item.Enable } : item
                )
            );
        } catch (error) {
            console.error("Toggle failed:", error);
        }
    };

    const popupTitle = secondLevel
        ? "Enable Second Level Commission"
        : "Disable Second Level Commission";

    const popupSubtext = secondLevel
        ? `The users below will be added as grandchildren to ${repUserName}. You can choose to include or exclude them during this assignment. Going forward all second level signups of ${repUserName} will be grandchildren.`
        : `The users below are existing grandchildren of ${repUserName}. You can choose to keep or remove them during this assignment. Going forward ${repUserName} will not be assigned new grandchildren.`;

    const allowColumnHeader = secondLevel ? "Make Grandchild" : "Keep Grandchild";

    return (
        <DialogPrimitive.Root open={true} onOpenChange={toggle_gp_popup}>
            <DialogPrimitive.Portal>
                {/* Overlay — z-[150] so it covers the back EditUser dialog content (z-[100]) */}
                <DialogPrimitive.Overlay
                    className={cn(
                        "fixed inset-0 z-[150] bg-black/90 backdrop-blur-sm",
                        "data-[state=open]:animate-in data-[state=closed]:animate-out",
                        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                    )}
                />
                {/* Content — z-[200] so it sits above the overlay */}
                <DialogPrimitive.Content
                    onInteractOutside={(e) => e.preventDefault()}
                    className={cn(
                        "fixed left-[50%] top-[50%] z-[200] w-full max-w-2xl",
                        "translate-x-[-50%] translate-y-[-50%]",
                        "bg-card border border-border rounded-lg shadow-lg p-4 sm:p-6",
                        "data-[state=open]:animate-in data-[state=closed]:animate-out",
                        "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
                        "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
                        "data-[state=closed]:slide-out-to-left-1/2 data-[state=open]:slide-in-from-left-1/2",
                        "data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%]"
                    )}
                >
                    {/* Close button */}
                    <DialogPrimitive.Close
                        className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
                        onClick={toggle_gp_popup}
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Close</span>
                    </DialogPrimitive.Close>

                    {/* Header */}
                    <div className="mb-4">
                        <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                            {popupTitle}
                        </DialogPrimitive.Title>
                    </div>

                    {/* Body */}
                    <div className="py-2">
                        <p className="text-sm text-muted-foreground mb-4">{popupSubtext}</p>

                        {grandparentsList?.length > 0 && (
                            <div className="border rounded-md overflow-hidden">
                                {/* Table Header */}
                                <div className="grid grid-cols-5 bg-muted px-3 py-2 text-xs font-semibold text-muted-foreground">
                                    <div>UserName</div>
                                    <div>FullName</div>
                                    <div>Email</div>
                                    <div>($)Comm.</div>
                                    <div>{allowColumnHeader}</div>
                                </div>
                                {/* Table Rows */}
                                <div
                                    className="max-h-60 overflow-y-auto"
                                    ref={tbodyRef}
                                >
                                    {grandparentsList.map((row, index) => (
                                        <AssignGrandParentRow
                                            key={index}
                                            gpData={row}
                                            handleToggleGP={handleToggleGP}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {errors?.length > 0 && (
                            <div className="mt-3 space-y-1">
                                {errors.map((item, index) => (
                                    <p key={index} className={item.code === '-1' ? "text-destructive text-sm" : "text-green-600 text-sm"}>
                                        {item.message}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                        <Button variant="outline" onClick={toggle_gp_popup} disabled={saveLoader}>
                            Cancel
                        </Button>
                        <Button onClick={AssignGPUsers} disabled={saveLoader}>
                            {saveLoader && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Proceed
                        </Button>
                    </div>
                </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
}

export default AssignAsGrandParent;


